import type {
  Citation,
  CitationDictionaryData,
  CitationLookupResult,
  CitationType,
  OutputLink,
} from "../types";

const EMPTY_RESULT: CitationLookupResult = { content: "" };

export function getCitationData(
  citation: Citation
): string | CitationDictionaryData | undefined {
  if (citation.citationData !== undefined) return citation.citationData;
  if (citation.citation_data !== undefined) return citation.citation_data;
  return undefined;
}

export function getCitationType(citation: Citation): CitationType | undefined {
  return citation.citationType ?? citation.citation_type;
}

export function isMarkdownCitation(citation: Citation): boolean {
  return getCitationType(citation) === "Markdown";
}

export function isDictionaryCitation(citation: Citation): boolean {
  return getCitationType(citation) === "Dictionary";
}

export function findCitationById(
  citations: Citation[] | undefined,
  currentId: string
): CitationLookupResult {
  if (!citations || citations.length === 0) return EMPTY_RESULT;

  const markdownResult = findMarkdownCitationById(citations, currentId);
  if (markdownResult) return markdownResult;

  const dictionaryResult = findDictionaryCitationById(citations, currentId);
  if (dictionaryResult) return dictionaryResult;

  return EMPTY_RESULT;
}

function findMarkdownCitationById(
  citations: Citation[],
  currentId: string
): CitationLookupResult | null {
  const matchingCitation = citations.find((citation) => {
    if (!isMarkdownCitation(citation)) return false;
    const data = getCitationData(citation);
    return typeof data === "string" && data.includes(currentId);
  });

  if (!matchingCitation) return null;

  const rawData = getCitationData(matchingCitation);
  if (typeof rawData !== "string") return null;

  const tableContent = extractTableContent(rawData, currentId);
  if (!tableContent) return null;

  return {
    content: tableContent,
    citationData: rawData,
    metaData: matchingCitation.metadata,
    currentId,
  };
}

function findDictionaryCitationById(
  citations: Citation[],
  currentId: string
): CitationLookupResult | null {
  const matchingCitation = citations.find((citation) => {
    if (!isDictionaryCitation(citation)) return false;
    const data = getCitationData(citation);
    if (typeof data !== "object" || data === null) return false;
    return Object.entries(data as CitationDictionaryData).some(
      ([key, link]) =>
        key === currentId ||
        (link && typeof link === "object" && link.text?.includes(currentId))
    );
  });

  if (!matchingCitation) return null;

  const citationData = getCitationData(matchingCitation);
  if (typeof citationData !== "object" || citationData === null) return null;

  const linkObject = findLinkObjectWithId(
    citationData as CitationDictionaryData,
    currentId
  );
  if (!linkObject) return null;

  return {
    content: `<i>${linkObject.text}</i>`,
    link: linkObject.link,
    metaData: linkObject.metadata,
    currentId,
    originalPdfUrl: linkObject.originalPdfUrl,
    highlights: linkObject.highlights,
  };
}

function extractTableContent(rawData: string, currentId: string): string | null {
  const allRows = rawData
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter((row) => row.length > 0);

  const tableRows = allRows.filter((row) => row.includes("|"));
  if (tableRows.length < 3) return null;

  const headerRow = tableRows[0];
  const separatorRow = tableRows.find((row) => /^\|[\s\-:|]*\|$/.test(row));
  const dataRow = tableRows.find((row) => row.includes(currentId));
  if (!separatorRow || !dataRow) return null;

  return `${headerRow}\n${separatorRow}\n${dataRow}`;
}

function findLinkObjectWithId(
  data: CitationDictionaryData,
  currentId: string
): OutputLink | null {
  for (const [key, link] of Object.entries(data)) {
    if (!link || typeof link !== "object") continue;
    if (key === currentId || link.text?.includes(currentId)) return link;
  }
  return null;
}
